import { useCallback, useState, type FormEvent } from 'react';
import { lookupPubChemSmiles, PubChemNotFoundError } from '@peruselab/api-client/pubchem';
import { isSmiles } from '@peruselab/structure-editor/chemistry/smilesImport';
import type { useSmilesImport } from './useSmilesImport';

/**
 * How the last lookup ended, told in this panel (#1059): the SMILES panel that reports a drawn
 * import isn't on screen when the Import menu opens PubChem alone. `asTyped`: PubChem had no
 * compound by that name, so the text itself was drawn as a SMILES.
 */
type PubChemLookupState =
  | { readonly state: 'idle' }
  | { readonly state: 'loading' }
  | { readonly state: 'imported'; readonly canonicalSmiles: string; readonly asTyped: boolean }
  | { readonly state: 'notDrawn'; readonly message: string; readonly asTyped: boolean }
  | { readonly state: 'error'; readonly message: string };

const CID_PATTERN = /^\d+$/;

export function usePubChemImport(smilesImport: ReturnType<typeof useSmilesImport>) {
  const [pubChemInput, setPubChemInput] = useState('');
  const [pubChemLookupState, setPubChemLookupState] = useState<PubChemLookupState>({ state: 'idle' });

  const draw = useCallback(
    async (smiles: string, asTyped: boolean) => {
      const outcome = await smilesImport.importSmilesString(smiles);
      setPubChemLookupState(
        outcome.state === 'success'
          ? { state: 'imported', canonicalSmiles: outcome.canonicalSmiles, asTyped }
          : { state: 'notDrawn', message: outcome.state === 'error' ? outcome.message : '', asTyped }
      );
    },
    [smilesImport]
  );

  const handlePubChemImport = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = pubChemInput.trim();
      if (!trimmed) return;
      setPubChemLookupState({ state: 'loading' });
      let smiles: string;
      try {
        smiles = await lookupPubChemSmiles(CID_PATTERN.test(trimmed) ? { cid: trimmed } : { name: trimmed });
      } catch (error) {
        // PubChem knows no such name, but the text is a SMILES (a name such as "NO" is asked
        // first, so it still means nitric oxide). Draw it as typed.
        if (error instanceof PubChemNotFoundError && !CID_PATTERN.test(trimmed) && (await isSmiles(trimmed))) {
          await draw(trimmed, true);
          return;
        }
        setPubChemLookupState({
          state: 'error',
          message: error instanceof Error ? error.message : 'Unable to look up this compound on PubChem.'
        });
        return;
      }
      await draw(smiles, false);
    },
    [pubChemInput, draw]
  );

  const updatePubChemInput = useCallback((value: string) => {
    setPubChemInput(value);
    setPubChemLookupState({ state: 'idle' });
  }, []);

  return { pubChemInput, updatePubChemInput, pubChemLookupState, handlePubChemImport };
}
